import { useState } from 'react';

import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';

import { useScreeningOutcome } from '@/hooks';
import type { MatchReviewDecision } from '@/types/match-review';
import {
  FINDING_KIND_LABELS,
  FINDING_SEVERITY_LABELS,
  summariseScreeningCounts,
  type ScreeningFinding,
  type SubjectScreeningOutcome,
} from '@/types/screening-outcome';
import { RatingBadge } from '../RatingBadge';

const DECIDED_BY_LABELS: Record<ScreeningFinding['decidedBy'], string> = {
  llm: 'Model',
  deterministic: 'Rule',
  analyst: 'Analyst',
  prior_run: 'Prior run',
  unknown: 'Unknown',
};

function confidence(value: number | null): string {
  return value === null ? '—' : `${Math.round(value * 100)}%`;
}

interface ReviewTarget {
  finding: ScreeningFinding;
  decision: MatchReviewDecision;
}

function FindingRow({
  finding,
  onReview,
}: Readonly<{
  finding: ScreeningFinding;
  onReview?: (target: ReviewTarget) => void;
}>) {
  const what = [
    FINDING_KIND_LABELS[finding.kind] ?? finding.kind,
    finding.category ? finding.categoryLabel : null,
    finding.severity ? `${FINDING_SEVERITY_LABELS[finding.severity]} severity` : null,
    finding.details.sanctions_related ? 'sanctions-related' : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Table.Tr>
      <Table.Td>
        <RatingBadge rating={finding.rating} size="xs" />
      </Table.Td>
      <Table.Td>
        <Stack gap={2}>
          {finding.sourceUrl ? (
            <Anchor
              href={finding.sourceUrl}
              target="_blank"
              rel="noreferrer"
              size="sm"
              lineClamp={2}
            >
              {finding.label ?? finding.sourceUrl}
            </Anchor>
          ) : (
            <Text size="sm" lineClamp={2}>
              {finding.label ?? '(no title)'}
            </Text>
          )}
          <Text size="xs" c="dimmed">
            {what}
            {finding.listSource ? ` · ${finding.listSource}` : ''}
            {finding.occurredAt ? ` · ${finding.occurredAt}` : ''}
          </Text>
        </Stack>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{confidence(finding.matchConfidence)}</Text>
        <Text size="xs" c="dimmed">
          {DECIDED_BY_LABELS[finding.decidedBy]}
        </Text>
      </Table.Td>
      {onReview ? (
        <Table.Td>
          <Group gap={4} wrap="nowrap" justify="flex-end">
            <Tooltip label="Confirm match — the record is about this subject">
              <ActionIcon
                variant="subtle"
                color="green"
                size="sm"
                aria-label="Confirm match"
                onClick={() => onReview({ finding, decision: 'confirm_match' })}
              >
                <IconCheck size={14} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Reject match — a re-run drops the record, and the rating and report follow">
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                aria-label="Reject match"
                onClick={() => onReview({ finding, decision: 'reject_match' })}
              >
                <IconX size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
      ) : null}
    </Table.Tr>
  );
}

function SubjectSection({
  subject,
  onReview,
}: Readonly<{
  subject: SubjectScreeningOutcome;
  onReview?: (target: ReviewTarget) => void;
}>) {
  return (
    <Stack gap="xs">
      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <Text fw={600} size="sm" lineClamp={1}>
            {subject.subjectName ?? 'Subject'}
          </Text>
          <RatingBadge
            rating={subject.rating}
            counts={subject.counts}
            provisional={subject.provisional}
            size="xs"
          />
          {subject.highRiskJurisdictions.length > 0 ? (
            <Tooltip
              label={`High-risk jurisdiction under the governing profile: ${subject.highRiskJurisdictions.join(', ')}`}
            >
              <Badge size="xs" color="orange" variant="light">
                HRJ · {subject.highRiskJurisdictions.join(' ')}
              </Badge>
            </Tooltip>
          ) : null}
        </Group>
        <Text size="xs" c="dimmed">
          {summariseScreeningCounts(subject.counts)}
        </Text>
      </Group>
      {subject.coverage.failed.length > 0 ? (
        <Badge size="xs" color="orange" variant="light">
          Partial: {subject.coverage.failed.join(', ')} failed
        </Badge>
      ) : null}
      {subject.provisional ? (
        <Text size="xs" c="dimmed">
          Provisional — {subject.pendingReviewCount} match review(s) pending for this subject.
        </Text>
      ) : null}
      {subject.findings && subject.findings.length > 0 ? (
        <Table verticalSpacing="xs" withRowBorders={false}>
          <Table.Tbody>
            {subject.findings.map((f) => (
              <FindingRow key={f.id} finding={f} onReview={onReview} />
            ))}
          </Table.Tbody>
        </Table>
      ) : (
        <Text size="sm" c="dimmed">
          Nothing retained by remediation.
        </Text>
      )}
    </Stack>
  );
}

/**
 * The report's screening outcome: the roll-up rating, each subject's rating
 * and, for admins, every retained finding with the rating the current risk
 * profile gives it — the "why" behind the badge — plus the triage actions
 * (confirm / reject a match) that fix a wrong retention from here.
 */
export function ScreeningOutcomePanel({ reportId }: Readonly<{ reportId: string }>) {
  const { outcome, isLoading, isMutating, error, review, applyVerdicts } =
    useScreeningOutcome(reportId);
  const [target, setTarget] = useState<ReviewTarget | null>(null);
  const [note, setNote] = useState('');

  if (isLoading && !outcome) {
    return (
      <Center h={120}>
        <Loader size="sm" />
      </Center>
    );
  }
  if (error) {
    return (
      <Text size="sm" c="red">
        Could not load the screening outcome: {error.message}
      </Text>
    );
  }
  if (!outcome || outcome.subjects.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Not rated yet — the rating is derived when remediation completes for a subject.
      </Text>
    );
  }
  const profile = outcome.subjects[0]?.profileSource;
  const profileName = typeof profile?.name === 'string' ? profile.name : null;
  const profileVersion = typeof profile?.version === 'number' ? profile.version : null;
  // The findings list is admin-only on the wire; its presence is what turns
  // the triage actions on.
  const isAdminView = outcome.subjects.some((s) => s.findings !== null);
  const pendingTotal = outcome.subjects.reduce((n, s) => n + s.pendingReviewCount, 0);

  const closeModal = () => {
    setTarget(null);
    setNote('');
  };
  const submitVerdict = async () => {
    if (!target) {
      return;
    }
    try {
      await review(target.finding.id, target.decision, note.trim() || null);
      closeModal();
    } catch {
      // the hook already notified; keep the modal open for a retry
    }
  };

  return (
    <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto">
      <Stack gap="md" pr="sm">
        <Group gap="xs">
          <Text fw={600}>Report rating</Text>
          <RatingBadge rating={outcome.screeningRating} />
          {profileName ? (
            <Text size="xs" c="dimmed">
              under {profileName}
              {profileVersion !== null ? ` v${profileVersion}` : ''}
            </Text>
          ) : null}
        </Group>
        {isAdminView && pendingTotal > 0 ? (
          <Group gap="xs" justify="space-between">
            <Text size="xs" c="dimmed">
              {pendingTotal} match review(s) pending on this report.
            </Text>
            <Button size="compact-xs" onClick={() => void applyVerdicts()} loading={isMutating}>
              Apply verdicts
            </Button>
          </Group>
        ) : null}
        {outcome.subjects.map((subject) => (
          <SubjectSection
            key={subject.subjectId}
            subject={subject}
            onReview={isAdminView ? setTarget : undefined}
          />
        ))}
      </Stack>
      <Modal
        opened={target !== null}
        onClose={closeModal}
        title={target?.decision === 'reject_match' ? 'Reject match' : 'Confirm match'}
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            {target?.decision === 'reject_match'
              ? 'The record is not about this subject. Applying the verdict re-runs remediation without it — the finding leaves the ledger, the rating and the rendered report follow.'
              : 'The record is about this subject. The confirmation is pinned so later runs never re-litigate it.'}
          </Text>
          <Text size="sm" fw={600} lineClamp={2}>
            {target?.finding.label ?? target?.finding.sourceUrl ?? 'Record'}
          </Text>
          <Textarea
            label="Note (optional)"
            placeholder="Why — kept with the verdict for the audit trail"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            minRows={2}
            autosize
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" size="compact-sm" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              size="compact-sm"
              color={target?.decision === 'reject_match' ? 'red' : 'green'}
              onClick={() => void submitVerdict()}
              loading={isMutating}
            >
              {target?.decision === 'reject_match' ? 'Record rejection' : 'Record confirmation'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </ScrollArea>
  );
}
